import { NodeProps, Position, Handle, useReactFlow, Edge } from 'reactflow';
import { Editor, EditorContent, JSONContent, useEditor } from '@tiptap/react';
import { CenteredSpinner } from '@duke-hq/app-admin-web/components/Loading';
import {
  Input,
  Box,
  IconButton,
  Tag,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Flex,
  HStack,
} from '@chakra-ui/react';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  KeyboardEvent,
  ChangeEvent,
  memo,
  useContext,
} from 'react';
import { FlowActionMenu } from './blocknode/FlowActionMenu';
import { Copy, MoreVertical, Pencil, Trash2 } from 'lucide-react';
import { createId } from '@paralleldrive/cuid2';
import { getBlockExtensions } from './blocknode/BlockTiptapExtensions';
import { BlockOptions } from './blocknode/BlockOptions';
import { SelectQuiz } from './blocknode/SelectQuiz';
import { toast } from '@duke-hq/app-admin-web/components/ToastContainer';
import { getFileDisplaySize } from '../tiptap/fileUtils';
import { FlowEditorStateContext } from '../FlowEditorStateContext';
import { MAXIMUM_FILE_SIZE_BYTES, BLOCK_NODE_WIDTH } from '../../constants';
import { NodeOutgoingMenu } from './blocknode/NodeOutgoingMenu';
import { sanitizeTiptapContent } from './sanitizeTiptapContent';
import { flowEditorState } from '../flowEditorState';
import { useSnapshot } from 'valtio';
import { subscribeKey } from 'valtio/utils';
import { isNodeDataContentEqual } from '../contentEquality';
import { TTEditorModeEnum, dukeTTEditorState } from '../dukeTTEditorState';
import { chatPersonaMenuState } from '../tiptap/flowDBlock/chatPersonaMenuState';
import { TextSelection, Transaction } from 'prosemirror-state';
import { EditorView } from '@tiptap/pm/view';
import { Node as ProseMirrorNode, NodeType } from '@tiptap/pm/model';
import { getIsStartNode, shouldRenderOutgoingMenu } from '../utils';
import { ContainerizedNodeOutgoingMenu } from './blocknode/ContainerizedNodeOutgoingMenu';
import { FileUploadExtension } from '../tiptap/FileUploadExtension';
import { useDukeFileUpload } from './blocknode/useDukeFileUpload';
import { courseEditorState } from '../../../courseEditorState';
import * as linkify from 'linkifyjs';
import { useDebounce } from 'use-debounce';
import {
  appendMessageBlock,
  chatMessageDBlock,
  hasMessageBubbles,
  inheritedPersonaId,
  isEmptyMessageBubble,
  type NodeData,
} from './flowEditorBlockNodeTypes';
import { pruneEmptyMessageBubbles } from './pruneEmptyMessageBubbles';
import { messageJSON } from '../tiptap/contentJSONs';
import { testIds as blockNodeTestIds } from './FlowEditorBlockNode/testIds';
import { usePersonaResolver } from '../PersonaResolverContext';
import { useTiptapFileSrcResolverFromContext } from '@duke-hq/ui/components/DukeTiptapEditor/useTiptapFileSrc';
import { StaticBlockContent } from './StaticBlockNode/StaticBlockContent';
import type { TiptapHighlight } from './StaticBlockNode/renderTiptapContent';
import { BlockNodeHeader } from './BlockNodeHeader';
import { brandColors } from '@duke-hq/ui/theme/chakraTheme';
import { useFindHighlight } from '../FindBar/FindHighlightContext';

/**
 * Finds option IDs present in the source handles within edges.
 * @param nodes - The nodes object array containing data with options to check.
 * @param edges - The array of edges to search for matching source handles.
 * @returns An array containing the IDs of options found in the source handles.
 */
export const checkOptionsConnected = (
  nodes: { data: Pick<NodeData, 'options'> }[],
  edges: Edge[],
): string[] => {
  const sourceHandles = new Set(
    edges.map((edge) => edge.sourceHandle).filter((handle) => handle !== undefined),
  );

  const connectedOptions: string[] = [];

  nodes.forEach((node) => {
    node.data.options?.forEach((option) => {
      if (sourceHandles.has(option.id)) {
        connectedOptions.push(option.id);
      }
    });
  });

  return connectedOptions;
};

type UploadView = Pick<EditorView, 'state' | 'dispatch' | 'isDestroyed'>;

export function findPlaceholderNode(
  view: UploadView,
  placeholderId: string,
): { pos: number; node: ProseMirrorNode } | null {
  let result: { pos: number; node: ProseMirrorNode } | null = null;
  view.state.doc.descendants((node, pos) => {
    if (result) {
      return false;
    }
    if (node.attrs.placeholderId === placeholderId) {
      result = { pos, node };
      return false;
    }
    return true;
  });
  return result;
}

export async function uploadAndPatchPlaceholder(
  view: UploadView,
  placeholderId: string,
  file: File,
  uploadFn: (file: File) => Promise<{ url: string; fileDrn?: string }>,
) {
  try {
    const { url, fileDrn } = await uploadFn(file);
    if (view.isDestroyed) {
      return;
    }
    const found = findPlaceholderNode(view, placeholderId);
    if (!found) {
      return;
    }
    view.dispatch(
      view.state.tr.setNodeMarkup(found.pos, undefined, {
        ...found.node.attrs,
        src: url,
        fileDrn,
        placeholderId: null,
      }),
    );
  } catch {
    if (view.isDestroyed) {
      return;
    }
    const found = findPlaceholderNode(view, placeholderId);
    if (found) {
      view.dispatch(view.state.tr.delete(found.pos, found.pos + found.node.nodeSize));
    }
  }
}

export function insertPlaceholderAndUpload(
  view: UploadView,
  nodeType: NodeType,
  attrs: Record<string, unknown>,
  file: File,
  uploadFn: (file: File) => Promise<{ url: string; fileDrn?: string }>,
) {
  const placeholderId = createId();
  const previousMessagePersonaId = view.state.doc.lastChild?.attrs.personaId;
  if (previousMessagePersonaId) {
    flowEditorState.defaultPersonaId = previousMessagePersonaId;
  }
  const node = nodeType.create({
    ...attrs,
    personaId: previousMessagePersonaId ?? flowEditorState.defaultPersonaId,
    src: null,
    fileDrn: null,
    placeholderId,
  });
  const tr = view.state.tr;
  view.dispatch(tr.insert(tr.doc.content.size, node));
  void uploadAndPatchPlaceholder(view, placeholderId, file, uploadFn);
}

// Scope Select-all (Mod-a) to the message bubble the cursor sits in, rather than
// the whole node. Without this, Mod-a selects every bubble and a following
// Backspace wipes the entire conversation node (and the empty result unmounts
// the editor, so it can't be undone). Bubbles should only be removed via the
// explicit Delete control. Returns true when it scoped the selection (so the
// caller can swallow the event); false when there is no enclosing bubble.
export function selectCurrentBubble(view: Pick<EditorView, 'state' | 'dispatch'>): boolean {
  const { state } = view;
  const { $head } = state.selection;

  for (let depth = $head.depth; depth > 0; depth--) {
    if ($head.node(depth).type.name === 'dBlock') {
      const selection = TextSelection.create(state.doc, $head.start(depth), $head.end(depth));
      view.dispatch(state.tr.setSelection(selection));
      return true;
    }
  }

  return false;
}

// When a Backspace/Delete would empty an entire message bubble (its whole content
// is selected), clear the text in place and keep the bubble rather than letting
// ProseMirror remove the bubble node. Bubbles are only removed via the explicit
// Delete control. Returns true when it handled the key, so the caller swallows it.
export function clearBubbleInsteadOfDeleting(
  view: Pick<EditorView, 'state' | 'dispatch'>,
): boolean {
  const { state } = view;
  const { selection } = state;
  const { $from } = selection;

  for (let depth = $from.depth; depth > 0; depth--) {
    if ($from.node(depth).type.name !== 'dBlock') {
      continue;
    }
    const start = $from.start(depth);
    const end = $from.end(depth);
    const wholeBubbleSelected = selection.from <= start && selection.to >= end;
    if (!wholeBubbleSelected) {
      return false;
    }
    const paragraphType = state.schema.nodes.paragraph;
    if (!paragraphType) {
      return false;
    }
    const tr = state.tr.replaceWith(start, end, paragraphType.create());
    view.dispatch(tr.setSelection(TextSelection.create(tr.doc, start + 1)));
    return true;
  }

  return false;
}

const HANDLE_STYLE = {
  width: '10px',
  height: '10px',
  zIndex: 1001,
};

// An option whose source handle has no outgoing edge yet. Tinting the handle
// amber (the theme's warning shade) points the author straight at the spot they
// still need to connect, the same options the publish check blocks on.
const UNCONNECTED_HANDLE_STYLE = {
  ...HANDLE_STYLE,
  background: brandColors.orange[500],
};

// Scoped to this editor instance (not a global stylesheet), so nothing leaks
// onto other surfaces that share these class names.
const editorSurfaceStyles = {
  '.tiptap': {
    marginLeft: 0,
    outline: 'none',
    caretColor: 'var(--chakra-colors-fg-default)',
  },
  // Spacing between message bubbles, matching the student runtime's globals.css.
  '.tiptap > * + *': { marginTop: 'var(--chakra-space-1)' },
  // Indent list markers to sit inside the bubble, matching the student runtime.
  '.tiptap ul, .tiptap ol': { marginLeft: 'var(--chakra-space-5)' },
  // Keeps the caret visible on an otherwise zero-height empty paragraph.
  '.tiptap p': { minHeight: '1.5em' },
  // The Placeholder extension's prompt. includeChildren lands the is-empty class
  // and data-placeholder on the <p> rather than the dBlock wrapper.
  '.tiptap p.is-empty::before': {
    content: 'attr(data-placeholder)',
    color: 'fg.muted',
    float: 'left',
    height: 0,
    pointerEvents: 'none',
  },
  // Links render as plain text while editing otherwise.
  '.tiptap a': { textDecoration: 'underline', cursor: 'pointer' },
  // Structural row layout for the message wrapper; its appearance is owned by
  // the shared ConversationMessage.
  '.content-bubble': { position: 'relative', display: 'flex', gap: '5px', width: '100%' },
  '.content-bubble p': { marginTop: 0, marginBottom: 0 },
  '.content-bubble .content': { cursor: 'text' },
  '[contenteditable="true"] [draggable="true"]': { cursor: 'grab' },
  '[contenteditable="true"] [draggable="true"]:active': { cursor: 'grabbing' },
};

/**
 * The editable half of a flow node. Mounts only while the node is selected, so
 * the heavy Tiptap editor (and its extensions and DOM handlers) exists for at
 * most one node at a time. Owns the live editing state; flushes its content
 * back to node.data before it unmounts so deselecting never drops the last
 * edit.
 */
function EditableNodeContent({
  id,
  data,
  isConnectable,
  dragging,
}: {
  id: string;
  data: NodeData;
  isConnectable: boolean;
  dragging: boolean;
}) {
  const [inputValue, setInputValue] = useState('');
  // Mirror of inputValue so the deselect/unmount cleanup reads the latest typed text,
  // committing a half-typed "Start typing" message on click-off instead of discarding it.
  const inputValueRef = useRef('');
  inputValueRef.current = inputValue;
  const [nodeData, setNodeData] = useState(data);

  const { setNodes, getEdges, deleteElements, getNode } = useReactFlow();
  const [inputDragActive, setInputDragActive] = useState(false);
  const { setActiveNode } = useContext(FlowEditorStateContext);

  const node = getNode(id);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => setInputValue(event.target.value);
  const { uploadFile: uploadFileFn } = useDukeFileUpload({
    fileAttributes: {
      attachedToKind: flowEditorState.resource?.id ? 'RESOURCE' : 'COURSE',
      attachedToId: flowEditorState.resource?.id ?? courseEditorState?.course?.id,
    },
  });
  const uploadFile = useCallback(
    async (file: File) => {
      const { filename, fileDrn } = await uploadFileFn(file);
      return { url: filename, fileDrn };
    },
    [uploadFileFn],
  );

  // Set while we write our own working copy into node.data, so the upstream sync
  // effect can tell its own echo from a genuine external change.
  const isInternalNodeWrite = useRef(false);

  // Mirror of nodeData read by the unmount flush and flushToNodeData, so they
  // see the final value rather than the one captured when the effect/callback
  // was registered.
  const nodeDataRef = useRef(nodeData);
  nodeDataRef.current = nodeData;

  // The restoreNonce seen at the last upstream-data sync. Lets the upstream effect
  // tell restored content from a genuine external edit and re-sync WITHOUT a dirty
  // bump (the restore already self-dirtied); a second bump would desync the stack.
  const restoreNonceRef = useRef(flowEditorState.restoreNonce);

  // Write the working copy into node.data now, bypassing the debounce. Called when
  // node.data must be authoritative: before an in-session undo snapshot, and on
  // unmount. The internal-write flag stops the upstream effect echoing it back.
  const flushToNodeData = useCallback(() => {
    setNodes((nodes) => {
      const node = nodes.find((n) => n.id === id);
      if (!node) {
        return nodes;
      }
      // The live editor owns ONLY `tiptap`; label/options/reply/etc. are edited
      // outside it, so flush only `tiptap`. Spreading the whole working copy reverts
      // those fields to their stale values.
      //
      // Return the SAME array when tiptap is unchanged: a fresh array retriggers the
      // post-commit recorder, which flushes again in a render loop.
      if (isNodeDataContentEqual(node.data, { ...node.data, tiptap: nodeDataRef.current.tiptap })) {
        return nodes;
      }
      isInternalNodeWrite.current = true;
      return nodes.map((n) =>
        n.id === id ? { ...n, data: { ...n.data, tiptap: nodeDataRef.current.tiptap } } : n,
      );
    });
  }, [id, setNodes]);

  // Apply a content change to the working copy and mark the flow dirty. Use for
  // mutations that touch node.data fields OTHER than tiptap (options, reply,
  // quiz). Writes the full `updated` data straight to the committed graph (these
  // menus are the authoritative writer for those fields), preserving the derived
  // outgoers. Done synchronously, not via the tiptap-only flush, so the change
  // reaches the committed graph immediately for the recorder + autosave.
  const commitNodeData = useCallback(
    (updated: NodeData) => {
      setNodeData(updated);
      nodeDataRef.current = updated;
      isInternalNodeWrite.current = true;
      setNodes((nodes) =>
        nodes.map((n) =>
          n.id === id ? { ...n, data: { ...updated, outgoers: n.data.outgoers } } : n,
        ),
      );
      flowEditorState.markDirty();
    },
    [id, setNodes],
  );

  const addOption = () => {
    const id = createId();
    const existingOptions = nodeData.options || [];
    const optionNumber = existingOptions.length + 1;

    const newOption = {
      id,
      title: `Option ${optionNumber}`,
    };

    const updatedData: NodeData = {
      ...nodeData,
      options: [...existingOptions, newOption],
    };

    commitNodeData(updatedData);
  };

  const onOptionReorder = (options: NodeData['options']) => {
    commitNodeData({ ...nodeData, options });
  };

  const removeOption = (optionId: string) => {
    const existingOptions = nodeData.options || [];

    const updatedOptions = existingOptions.filter((option) => option.id !== optionId);

    if (updatedOptions.length === 0) {
      const { options, ...rest } = nodeData;
      commitNodeData(rest);
    } else {
      commitNodeData({ ...nodeData, options: updatedOptions });
    }

    const edges = getEdges();
    const edgeToRemove = edges.find((edge) => edge.sourceHandle === optionId && edge.source === id);
    // // Remove the edge associated with the option's handle
    if (edgeToRemove) {
      deleteElements({ edges: [edgeToRemove] });
    }
  };

  function textToTiptapParagraph(text: string) {
    // Use linkify to find links in the text
    const matches = linkify.find(text);
    if (!matches.length) {
      return {
        type: 'paragraph',
        content: [
          {
            type: 'text',
            text,
          },
        ],
      };
    }

    const content: JSONContent[] = [];
    let lastIndex = 0;
    matches.forEach((match) => {
      if (match.start > lastIndex) {
        content.push({
          type: 'text',
          text: text.slice(lastIndex, match.start),
        });
      }
      content.push({
        type: 'text',
        text: match.value,
        marks: [
          {
            type: 'link',
            attrs: {
              href: match.href,
            },
          },
        ],
      });
      lastIndex = match.end;
    });
    if (lastIndex < text.length) {
      content.push({
        type: 'text',
        text: text.slice(lastIndex),
      });
    }
    return {
      type: 'paragraph',
      content,
    };
  }

  function addTextAsDBlockIntoTTEditor(text: string[]) {
    if (editor) {
      const messages: JSONContent[] = text
        .filter((t) => {
          return !!t; // empty text nodes are not allowed
        })
        .map((t) => {
          return textToTiptapParagraph(t);
        });
      const root = editor.getJSON();
      const personaId = inheritedPersonaId(root);

      // An options-only block serialises to `{ type: 'doc' }` with no `content`
      // key, and a fresh node holds a single empty starter paragraph. Both should
      // start from an empty bubble list, otherwise the pushed messages are either
      // dropped (push against undefined) or appended after a stray paragraph.
      const firstBlock = root.content?.[0];
      if (
        !root.content?.length ||
        (firstBlock && Object.keys(firstBlock).length === 1 && firstBlock.type === 'paragraph')
      ) {
        root.content = [];
      }

      for (const msg of messages) {
        root.content.push(chatMessageDBlock(msg, personaId));
      }
      editor.commands.setContent(root);
      if (root) {
        const updated = { ...nodeData, tiptap: root };
        setNodeData(updated);
        nodeDataRef.current = updated;
        flushToNodeData();
        flowEditorState.markDirty();
      }
    }
  }

  const editor = useEditor({
    // Don't re-render this component on every editor transaction. ProseMirror
    // renders content changes to the DOM itself, and nothing here reads live
    // selection/command state at render time, so a selection change (e.g. arrow
    // keys) no longer needs to re-render the node and its header chrome.
    shouldRerenderOnTransaction: false,
    extensions: [
      ...getBlockExtensions(),
      FileUploadExtension.configure({
        limit: 50,
        upload: uploadFile,
        maxSize: MAXIMUM_FILE_SIZE_BYTES,
        onError: (error) => {
          toast({
            title: error.message,
            status: 'error',
          });
        },
      }),
    ],
    content: nodeData.tiptap,
    onUpdate: onTipTapUpdate((tipTapContent, { isStructural }) => {
      const updated = { ...nodeData, tiptap: tipTapContent };
      setNodeData(updated);
      nodeDataRef.current = updated;
      // A structural edit (bubble added, deleted, reordered) must reach node.data on
      // the SAME tick it marks dirty, or the recorder reads the pre-edit graph and
      // gates it as a no-op. Plain typing stays on the debounce (no per-keystroke
      // canvas re-render); noteTextEdit tells the recorder this node owns the run.
      if (isStructural) {
        flushToNodeData();
      } else {
        flowEditorState.noteTextEdit(id);
      }
      flowEditorState.markDirty();
    }),
    editorProps: {
      attributes: {
        parentId: id,
      },
      handleDOMEvents: {
        keydown: (view, event) => {
          const nodeId = view.dom.getAttribute('parentId');

          if (!nodeId) {
            return true; // do not handle, let the editor handle it
          }

          const currentEditor = flowEditorState.nodeIdToEditorMap.get(nodeId);

          if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'a') {
            if (selectCurrentBubble(view)) {
              event.preventDefault();
              return true;
            }
          }

          if (event.key === 'Enter') {
            const isListActive =
              currentEditor?.isActive('bulletList') || currentEditor?.isActive('orderedList');
            // we want a new paragraph on enter wrapped within the messageStyleWrapper
            if (!isListActive) {
              if (event.shiftKey) {
                currentEditor?.commands.insertContent('<br/>');
                return true; // do not handle, let the editor handle it
              } else {
                // Insert the same empty message bubble the add-block menu's
                // "Message" item adds (placeholder + caret), so Enter and the
                // menu behave identically.
                event.preventDefault();
                updateContent(chatMessageDBlock(messageJSON));
                return true;
              }
            }
          }

          if (['Backspace', 'Delete'].includes(event.key) && currentEditor) {
            if (clearBubbleInsteadOfDeleting(view)) {
              event.preventDefault();
              return true;
            }

            const text = currentEditor.getText()?.trim();
            const json = sanitizeTiptapContent(currentEditor.getJSON());
            const singleBlock = json.content?.length === 1;
            const block = json.content?.[0];

            if (!singleBlock || !block?.content) {
              return false;
            }

            // Prevent removing the last paragraph when there's only one content block and user presses Cmd+Delete or Backspace
            if (event.metaKey || text?.length === 1) {
              event.preventDefault();
              event.stopPropagation();

              block.content = [
                {
                  type: 'paragraph',
                  content: [
                    {
                      type: 'text',
                      text: '‎', // invisible character to preserve paragraph
                    },
                  ],
                },
              ];

              setNodeData({ ...nodeData, tiptap: json });
              currentEditor.commands.setContent(json);

              return true;
            }
          }
        },
        paste: (view) => {
          const nodeId = view.dom.getAttribute('parentId');

          if (!nodeId) {
            return true; // do not handle, let the editor handle it
          }

          const currentEditor = flowEditorState.nodeIdToEditorMap.get(nodeId);
          // paste was invoked on the editor. We let the editor handle it, but we want to wrap all paragraphs that are pasted in dBlock
          setTimeout(() => {
            // this is slightly hacky, but it works ok in our limited testing so far
            if (currentEditor) {
              const sanitized = sanitizeTiptapContent(currentEditor.getJSON());
              setNodeData({ ...nodeData, tiptap: sanitized });
              currentEditor.commands.setContent(sanitized);
            }
          }, 20);
        },
        dragstart: (_editorView, event) => {
          const elem = (_editorView as any)?.lastSelectedViewDesc?.dom?.querySelector(
            '[data-draggable]',
          );
          elem.id = 'drag-ghost';
          elem.textNode = 'Dragging';

          event.dataTransfer?.setDragImage(elem, 0, 0);
        },
      },
      handleDrop: function (view, event, _slice, moved) {
        if (
          moved ||
          !event.dataTransfer ||
          !event.dataTransfer.files ||
          !event.dataTransfer.files[0]
        ) {
          return false;
        }
        event.preventDefault();

        const { schema } = view.state;
        const file = event.dataTransfer.files[0];
        const { displayUnit, displaySize } = getFileDisplaySize(file);
        const isMedia = file.type.includes('image') || file.type.includes('video');

        if (isMedia) {
          const withinLimit = parseFloat(displaySize) <= 3 || displayUnit === 'KB';
          if (!withinLimit || !schema.nodes.resizableMedia) {
            toast({ title: 'Maximum image size is 3MB', status: 'warning' });
            return true;
          }
          insertPlaceholderAndUpload(
            view,
            schema.nodes.resizableMedia,
            {
              title: file.name,
              alt: file.name,
              type: file.type,
              'media-type': file.type.includes('image') ? 'img' : 'video',
            },
            file,
            uploadFile,
          );
          return true;
        }

        const withinLimit = parseFloat(displaySize) <= 2 || displayUnit === 'KB';
        if (!withinLimit || !schema.nodes.fileComponent) {
          toast({ title: 'Maximum file size is 2MB', status: 'warning' });
          return true;
        }
        insertPlaceholderAndUpload(
          view,
          schema.nodes.fileComponent,
          {
            size: displaySize,
            sizeUnit: displayUnit,
            title: file.name,
            type: file.type,
          },
          file,
          uploadFile,
        );
        return true;
      },
    },
  });

  const updateContent = (newData: JSONContent) => {
    if (!newData || !editor) {
      return;
    }

    const currentRoot = editor.getJSON();
    // Double Enter would wedge a second empty bubble: refocus the trailing
    // placeholder instead. Substantive adds fall through and fill it.
    const lastBlock = currentRoot.content?.[currentRoot.content.length - 1];
    if (lastBlock && isEmptyMessageBubble(lastBlock) && isEmptyMessageBubble(newData)) {
      editor.commands.focus('end');
      return;
    }
    // Keep the inherited persona as the editor-wide default for the next append.
    if (!editor.isEmpty && currentRoot.content?.length) {
      const personaId = inheritedPersonaId(currentRoot);
      if (personaId) {
        flowEditorState.defaultPersonaId = personaId;
      }
    }
    const wasEmpty = editor.isEmpty;
    const root = appendMessageBlock(currentRoot, newData, wasEmpty);

    editor.commands.setContent(root);
    // Land the caret in the new block. Focus synchronously when the editor already had
    // content (DOM mounted); the empty -> first-bubble case needs the rAF defer because
    // the DOM mounts only once `!editor.isEmpty` flips next render.
    if (wasEmpty) {
      requestAnimationFrame(() => editor.commands.focus('end'));
    } else {
      editor.commands.focus('end');
    }

    if (root) {
      const updated = { ...nodeData, tiptap: root };
      setNodeData(updated);
      // Sync the ref so the flush below writes the new content, not the pre-add value.
      nodeDataRef.current = updated;
      // Flush so the add is authoritative when the recorder captures off the dirty signal.
      flushToNodeData();
      // A content-bearing add (the "Start typing" input, a paste) never goes through
      // the keystroke path, so open a session to couple it into the typing run and
      // keep undo one-press-per-message.
      //
      // An empty placeholder begins the NEXT message, so close the run NOW to finalise
      // the completed message as its own entry (see endTextRunNow).
      if (isEmptyMessageBubble(newData)) {
        flowEditorState.endTextRunNow();
      } else {
        flowEditorState.noteTextEdit(id);
      }
      flowEditorState.markDirty();
    }
  };
  const handleDrop = (event: React.DragEvent<HTMLInputElement>) => {
    setInputDragActive(false);
    event.preventDefault();
    event.stopPropagation();

    if (!editor || !event.dataTransfer?.files?.[0]) {
      return;
    }
    const file = event.dataTransfer.files[0];
    const { displayUnit, displaySize } = getFileDisplaySize(file);
    const schema = editor.view.state.schema;
    const isMedia = file.type.includes('image') || file.type.includes('video');

    if (isMedia) {
      const withinLimit = parseFloat(displaySize) <= 3 || displayUnit === 'KB';
      if (!withinLimit || !schema.nodes.resizableMedia) {
        toast({ title: 'Maximum image size is 3MB', status: 'warning' });
        return;
      }
      flushToNodeData();
      insertPlaceholderAndUpload(
        editor.view,
        schema.nodes.resizableMedia,
        {
          title: file.name,
          alt: file.name,
          type: file.type,
          'media-type': file.type.includes('image') ? 'img' : 'video',
        },
        file,
        uploadFile,
      );
      return;
    }

    const withinLimit = parseFloat(displaySize) <= 2 || displayUnit === 'KB';
    if (!withinLimit || !schema.nodes.fileComponent) {
      toast({ title: 'Maximum file size is 2MB', status: 'warning' });
      return;
    }
    flushToNodeData();
    insertPlaceholderAndUpload(
      editor.view,
      schema.nodes.fileComponent,
      {
        size: displaySize,
        sizeUnit: displayUnit,
        title: file.name,
        type: file.type,
      },
      file,
      uploadFile,
    );
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    const message: JSONContent = {
      type: 'paragraph',
      content: [
        {
          type: 'text',
          text: inputValue,
        },
      ],
    };
    if (event.key === 'Enter') {
      if (!inputValue) {
        event.preventDefault();
        event.stopPropagation();
        return false;
      }
      updateContent(chatMessageDBlock(message));
      setInputValue('');
    } else {
      return true;
    }
  };

  // Live sync of the working copy into node.data while the editor is open. Debounced
  // so a large flow doesn't reconcile every node per keystroke; the unmount flush is
  // the final write. The internal-write flag stops the upstream effect echoing it back.
  const [debouncedNodeData, debouncedNodeDataControl] = useDebounce(nodeData, 300);
  useEffect(() => {
    if (data !== debouncedNodeData) {
      setNodes((nodes) => {
        const node = nodes.find((n) => n.id === id);
        if (!node) {
          return nodes;
        }
        // Write only `tiptap` (see flushToNodeData) and gate on a tiptap-only change,
        // or an equal-but-new-reference tick churns the graph and retriggers the recorder.
        const merged = { ...node.data, tiptap: debouncedNodeData.tiptap };
        if (isNodeDataContentEqual(node.data, merged)) {
          return nodes;
        }
        isInternalNodeWrite.current = true;
        return nodes.map((n) => (n.id === id ? { ...n, data: merged } : n));
      });
    }
  }, [debouncedNodeData]);

  useEffect(() => {
    if (id && editor) {
      flowEditorState.setNodeIdToEditorMap(id, editor);
    }

    return () => {
      flowEditorState.deleteNodeIdToEditorMap(id);
    };
  }, [id, editor]);

  // Expose this node's flush/cancel/read to the recorder while it is the live editor,
  // so the recorder can force the working copy into node.data before it snapshots.
  useEffect(() => {
    flowEditorState.flushActiveEditor = flushToNodeData;
    flowEditorState.cancelActiveEditorDebounce = debouncedNodeDataControl.cancel;
    // nodeDataRef holds the current tiptap before the debounce commits; the recorder
    // overlays it when stashing for undo/redo so redo restores the exact text.
    flowEditorState.getActiveEditorContent = () => ({ id, tiptap: nodeDataRef.current.tiptap });
    return () => {
      flowEditorState.flushActiveEditor = () => {};
      flowEditorState.cancelActiveEditorDebounce = () => {};
      flowEditorState.getActiveEditorContent = () => null;
    };
  }, [flushToNodeData, debouncedNodeDataControl.cancel, id]);

  useEffect(() => {
    if (isInternalNodeWrite.current) {
      // Our own debounced sync echoing back, not an upstream edit; leave the working
      // copy alone.
      isInternalNodeWrite.current = false;
      return;
    }
    // A restore writes restored content onto `data`. Re-sync from it but do NOT
    // markDirty or steal selection: the restore already self-dirtied, and a second
    // bump desyncs the undo stack. Detect it by the restoreNonce advancing.
    const fromRestore = flowEditorState.restoreNonce !== restoreNonceRef.current;
    restoreNonceRef.current = flowEditorState.restoreNonce;

    // Compare by content, not reference: an upstream write can rebuild data with a new
    // object carrying identical content, which a reference check would mark dirty.
    if (!isNodeDataContentEqual(data, nodeData)) {
      setNodeData(data);
      if (!fromRestore) {
        flowEditorState.markDirty('upstream-data-effect');
        dukeTTEditorState.mode = TTEditorModeEnum.EDIT;
        if (node) {
          setActiveNode(node);
        }
      }
    }
  }, [data]);

  // Flush the working copy into node.data on deselect, so typing then clicking away
  // doesn't lose the last keystrokes (the live sync is debounced).
  //
  // Commit half-typed "Start typing" text first (it only commits on Enter otherwise),
  // then drop empty placeholder bubbles. Prune only on deselect, never while editing,
  // or the bubble you just made room to type in vanishes.
  useEffect(() => {
    return () => {
      const halfTyped = inputValueRef.current.trim();
      if (halfTyped) {
        const message = chatMessageDBlock({
          type: 'paragraph',
          content: [{ type: 'text', text: inputValueRef.current }],
        });
        const root = nodeDataRef.current.tiptap ?? { type: 'doc', content: [] };
        nodeDataRef.current = {
          ...nodeDataRef.current,
          tiptap: appendMessageBlock(root, message, !root.content?.length),
        };
        flowEditorState.markDirty();
      }
      const pruned = pruneEmptyMessageBubbles(nodeDataRef.current.tiptap);
      if (pruned) {
        nodeDataRef.current = { ...nodeDataRef.current, tiptap: pruned };
      }
      flushToNodeData();
    };
  }, [flushToNodeData]);

  if (!editor || !nodeData) {
    return <CenteredSpinner />;
  }

  if (!node) {
    return null;
  }

  return (
    <>
      {/* StaticBlockContent's tinted panel, so selection doesn't change the
          surface. Rendered only with content: a blank node shows just the
          "Start typing" input as the single place to add a message. An empty
          placeholder bubble counts as content (editor.isEmpty disagrees) or a
          Message add would never appear. */}
      {(!editor.isEmpty || hasMessageBubbles(editor.getJSON())) && (
        <Box
          // nodrag stops ReactFlow's canvas pointer/pan handlers from
          // intercepting clicks here, which otherwise steal focus from the
          // editor and leave the caret invisible.
          className="nodrag"
          bg="bg.subtle"
          borderRadius="md"
          p={2}
          sx={editorSurfaceStyles}
        >
          <EditorContent editor={editor} />
        </Box>
      )}

      <HStack
        pl={1}
        justifyContent="start"
        mt={1}
        onClick={(ev) => {
          ev.stopPropagation();
        }}
      >
        <FlowActionMenu
          node={node}
          nodeData={nodeData}
          setNodeData={commitNodeData}
          editor={editor}
          updateContent={updateContent}
        />
        <Input
          id="flow-editor-message-input"
          borderColor={inputDragActive ? 'blue.500' : 'gray.300'}
          bg={inputDragActive ? 'blue.100' : 'white'}
          onDragEnter={() => setInputDragActive(true)}
          onDragOver={() => setInputDragActive(true)}
          onDragExit={() => setInputDragActive(false)}
          onKeyDown={handleKeyDown}
          onPaste={(ev) => {
            const pastedText = ev.clipboardData.getData('text');
            const chunks = pastedText.split('\n');
            addTextAsDBlockIntoTTEditor(chunks);
            ev.preventDefault();
            return false;
          }}
          variant="unstyled"
          value={inputValue}
          onChange={handleChange}
          onDrop={handleDrop}
          placeholder="Start typing"
          autoComplete="off"
        />
      </HStack>
      {nodeData.quizId && flowEditorState.nodeIdToEditorMap.has(id) ? (
        <SelectQuiz
          setNodeData={commitNodeData}
          node={node}
          isConnectable={isConnectable}
          nodeId={id}
        />
      ) : (
        <BlockOptions
          nodeData={nodeData}
          isConnectable={isConnectable}
          nodeId={id}
          addOption={addOption}
          removeOption={removeOption}
          onOptionReorder={onOptionReorder}
          dragging={dragging}
        />
      )}
    </>
  );
}

/**
 * The read-only half of a flow node. Always mounted (for unselected nodes), with
 * no Tiptap editor: renders the static card and the source handles for each
 * option so edges stay anchored while the node is not being edited.
 */
function StaticNodeContent({
  data,
  isConnectable,
  highlight,
}: {
  data: NodeData;
  isConnectable: boolean;
  highlight?: TiptapHighlight;
}) {
  const { getPersonaById } = usePersonaResolver();
  // The same fileDrn-aware resolution the live editor's node views use
  // (useTiptapFileSrc), so static and live media URLs can't drift.
  const resolveFileSrc = useTiptapFileSrcResolverFromContext();
  // useSnapshot keeps connectedOptionIds reactive: this component re-renders,
  // and renderOptionHandle's identity changes (breaking StaticBlockContent's
  // memo), whenever an option connects or disconnects.
  const { connectedOptionIds } = useSnapshot(flowEditorState);

  // Stable identities so the memoised StaticBlockContent can skip re-rendering
  // when an unrelated edit replaces the nodes array. The resolvers they close
  // over are themselves stable.
  const resolveMediaSrc = useCallback(
    (src: string, fileDrn?: unknown) => resolveFileSrc(fileDrn, `/files/${src}`),
    [resolveFileSrc],
  );
  const renderOptionHandle = useCallback(
    (optionId: string) => (
      <Handle
        type="source"
        position={Position.Right}
        id={optionId}
        // Tint the handle amber until this option has an outgoing edge, so the
        // author can see at a glance which options the publish check blocks on.
        style={connectedOptionIds.has(optionId) ? HANDLE_STYLE : UNCONNECTED_HANDLE_STYLE}
        isConnectable={isConnectable}
      />
    ),
    [isConnectable, connectedOptionIds],
  );

  return (
    <StaticBlockContent
      tiptap={data.tiptap}
      options={data.options}
      getPersonaById={getPersonaById}
      resolveMediaSrc={resolveMediaSrc}
      renderOptionHandle={renderOptionHandle}
      highlight={highlight}
    />
  );
}

/**
 * A flow editor node. Always renders the lightweight frame (border, header,
 * actions menu, handles); the heavy Tiptap editor lives in EditableNodeContent
 * and is mounted only when this node is selected, so unselected nodes are cheap.
 */
function FlowEditorBlockNode({ id, selected, dragging, data, isConnectable }: NodeProps<NodeData>) {
  const { setNodes, deleteElements, getNode } = useReactFlow();
  const [isHoveredOnNode, setIsHoveredOnNode] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);

  const { query: findQuery, currentNodeId, matchedNodeIds, currentRun } = useFindHighlight();
  const highlight = matchedNodeIds.has(id)
    ? { query: findQuery, currentRun: currentNodeId === id ? currentRun : -1 }
    : undefined;

  // Remount the editable subtree on each restore so it rebuilds from the restored
  // node.data rather than keeping buffered local state and uncontrolled inputs.
  // subscribeKey, not useSnapshot, so a keystroke never re-renders this node.
  const [restoreKey, setRestoreKey] = useState(0);
  useEffect(() => {
    return subscribeKey(flowEditorState, 'restoreNonce', () => {
      setRestoreKey((key) => key + 1);
    });
  }, []);

  useEffect(() => {
    flowEditorState.setIsConnectable(isConnectable);
  }, [isConnectable]);

  const node = getNode(id);

  const isStartNode = node && getIsStartNode(node);
  const isEndNode = node && flowEditorState.endNodeIds.has(id);

  const showOutgoingMenu = shouldRenderOutgoingMenu(node);

  // Rename the node from its header label. Writes straight to node.data and
  // marks the flow dirty, the same path genuine edits take.
  const handleLabelChange = (value: string) => {
    const newLabel = value.trim();
    if (newLabel === '' || newLabel === data.label) {
      return;
    }
    setNodes((nodes) =>
      nodes.map((n) => (n.id === id ? { ...n, data: { ...n.data, label: newLabel } } : n)),
    );
    flowEditorState.markDirty();
  };

  if (!node) {
    return null;
  }

  return (
    <>
      {isStartNode || isEndNode ? (
        <Tag
          backgroundColor="black"
          color="white"
          textTransform="uppercase"
          fontWeight="bold"
          fontSize="xs"
          m={1}
        >
          {isStartNode ? 'Start' : 'End'}
        </Tag>
      ) : null}

      <Flex
        data-testid={blockNodeTestIds.node}
        border={selected ? '1px solid #563FD8' : '1px solid #E2E8F0'}
        flexDir="column"
        borderRadius="8px"
        bgColor="white"
        className="nowheel"
        onMouseEnter={() => setIsHoveredOnNode(true)}
        onMouseLeave={() => setIsHoveredOnNode(false)}
        boxShadow="md"
        h="100%"
        cursor="auto"
        minW={BLOCK_NODE_WIDTH}
        maxW={BLOCK_NODE_WIDTH}
        m={1}
        pos="relative"
        onClick={() => {
          chatPersonaMenuState.close();
        }}
      >
        {showOutgoingMenu ? (
          node.parentNode ? (
            <ContainerizedNodeOutgoingMenu node={node} isHoveredOnNode={isHoveredOnNode} />
          ) : (
            <NodeOutgoingMenu node={node} isHoveredOnNode={isHoveredOnNode} />
          )
        ) : null}

        <BlockNodeHeader
          label={data.label}
          isRenaming={isRenaming}
          onRename={isStartNode ? undefined : handleLabelChange}
          onRenameEnd={isStartNode ? undefined : () => setIsRenaming(false)}
          onStartRename={isStartNode ? undefined : () => setIsRenaming(true)}
          actions={
            isStartNode ? null : (
              <Box
                zIndex={1010}
                onClick={(ev) => {
                  ev.stopPropagation();
                }}
              >
                <Menu
                  onOpen={() => {
                    setNodes((nodes) =>
                      nodes.map((node) => {
                        if (node.id == id) {
                          node.selected = true;
                        } else {
                          node.selected = false;
                        }
                        return node;
                      }),
                    );
                    setIsMenuOpen(true);
                    chatPersonaMenuState.close();
                  }}
                  onClose={() => setIsMenuOpen(false)}
                >
                  <MenuButton
                    as={IconButton}
                    aria-label="Node actions"
                    icon={<MoreVertical size={18} />}
                    variant="ghost"
                    size="xs"
                    data-testid={blockNodeTestIds.nodeActionsButton}
                  />
                  {isMenuOpen ? (
                    <MenuList w={'170px'}>
                      <MenuItem icon={<Pencil width={24} />} onClick={() => setIsRenaming(true)}>
                        Rename
                      </MenuItem>
                      <MenuItem
                        icon={<Copy width={24} />}
                        onClick={() => {
                          const node = getNode(id);

                          if (node) {
                            setNodes((nodes) => [
                              ...nodes.map((node) => ({
                                ...node,
                                selected: false,
                              })),
                              {
                                ...node,
                                id: createId(),
                                position: {
                                  x: node.position.x + 50,
                                  y: node.position.y + 50,
                                },
                              },
                            ]);
                            flowEditorState.markDirty();
                          }
                        }}
                      >
                        Duplicate
                      </MenuItem>
                      <MenuItem
                        icon={<Trash2 />}
                        onClick={() => {
                          deleteElements({ nodes: [{ id }] });
                        }}
                      >
                        Delete
                      </MenuItem>
                    </MenuList>
                  ) : null}
                </Menu>
              </Box>
            )
          }
        />

        <Box p={2}>
          {selected ? (
            <EditableNodeContent
              key={restoreKey}
              id={id}
              data={data}
              isConnectable={isConnectable}
              dragging={dragging}
            />
          ) : (
            <StaticNodeContent data={data} isConnectable={isConnectable} highlight={highlight} />
          )}
        </Box>

        {!isStartNode ? (
          <Handle
            type="target"
            position={Position.Left}
            style={HANDLE_STYLE}
            isConnectable={isConnectable}
          />
        ) : null}
        {data.options ? null : (
          <Handle
            type="source"
            position={Position.Right}
            id="b"
            style={HANDLE_STYLE}
            isConnectable={isConnectable}
          />
        )}
      </Flex>
    </>
  );
}

export const MemoizedBlockNode = memo(FlowEditorBlockNode);

export function onTipTapUpdate(
  onDone: (content: JSONContent, opts: { isStructural: boolean }) => void,
) {
  return ({ editor, transaction }: { editor: Editor; transaction: Transaction }) => {
    // Doc-preserving transactions (selection, focus/blur) still fire onUpdate but
    // aren't edits; skip them so a bare click doesn't mark the flow dirty.
    if (!transaction.docChanged) {
      return;
    }

    const json = editor.getJSON();
    // ProseMirror omits `content` for an empty doc (last bubble deleted). That is a
    // real, undoable edit, so default to an empty array rather than bailing.
    const content = json.content ?? [];

    // Only dBlocks are valid message bubbles. Typing into a fresh empty node lands in
    // a bare starter paragraph; promote a content-bearing one into a dBlock (dropping
    // only EMPTY bare paragraphs), or the first thing typed is silently eaten. The
    // promoted bubble inherits the preceding bubble's persona (else the editor default).
    const normalisedContent: JSONContent[] = [];
    for (const child of content) {
      if (child.type !== 'paragraph') {
        normalisedContent.push(child);
        continue;
      }
      if (!child.content?.length) {
        continue;
      }
      const personaId = inheritedPersonaId({ ...json, content: normalisedContent });
      normalisedContent.push(chatMessageDBlock(child, personaId));
    }

    const tipTapContent = {
      ...json,
      content: normalisedContent,
    };

    // Re-applying content moves the cursor, so only do it when the structure changed,
    // and restore the caret to the end so typing continues uninterrupted.
    const structureChanged =
      normalisedContent.length !== content.length ||
      normalisedContent.some((c: JSONContent, i) => c.type !== content[i]?.type);
    if (structureChanged) {
      editor.commands.setContent(tipTapContent, false);
      editor.commands.focus('end');
    }

    // isStructural: this transaction changed the BLOCK structure (bubble added,
    // removed, reordered) rather than text within a bubble. Structural edits flush to
    // node.data immediately so the recorder sees them on the dirty tick; a keystroke
    // keeps the debounced sync (per-keystroke flushing re-lags the canvas).
    onDone(tipTapContent, {
      isStructural: isStructuralChange(transaction.before, editor.state.doc),
    });
  };
}

// Per-block fingerprint (text only) of a doc's top-level children, used to tell a
// structural bubble change from a text edit.
function topLevelBlockTexts(doc: ProseMirrorNode): string[] {
  const texts: string[] = [];
  doc.forEach((child) => texts.push(child.textContent));
  return texts;
}

// The personaId of each top-level block in order, so a persona change (same
// blocks, same text, one block's persona rewritten) is detectable.
function topLevelBlockPersonaIds(doc: ProseMirrorNode): string[] {
  const ids: string[] = [];
  doc.forEach((child) =>
    ids.push(typeof child.attrs.personaId === 'string' ? child.attrs.personaId : ''),
  );
  return ids;
}

// Whether a transaction changed the BLOCK STRUCTURE (a bubble added, removed, or
// reordered) or a bubble's persona, rather than text within a bubble. Add/remove
// changes the block count; a reorder keeps the same multiset of block texts but
// in a new order; a persona change rewrites a block's personaId attr in place.
// Typing changes one block's text in place: same count, and the multiset is NOT
// preserved (the edited block's text is new), so it reads as NOT structural and
// stays on the debounced sync. Structural edits flush immediately so the undo
// recorder sees them on the tick the dirty signal fires; a persona change has the
// same requirement (it bumps the dirty signal synchronously, but its content
// would otherwise reach the committed graph only via the 300ms debounced sync, by
// which time the recorder has already consumed the bump and gated the change as a
// no-op). A persona change is a discrete action, not per-keystroke typing, so an
// immediate flush for it does not reintroduce the typing-lag the debounce avoids.
export function isStructuralChange(before: ProseMirrorNode, after: ProseMirrorNode): boolean {
  if (before.childCount !== after.childCount) {
    return true;
  }
  const beforePersonaIds = topLevelBlockPersonaIds(before);
  const afterPersonaIds = topLevelBlockPersonaIds(after);
  if (beforePersonaIds.some((personaId, i) => personaId !== afterPersonaIds[i])) {
    return true;
  }
  const beforeTexts = topLevelBlockTexts(before);
  const afterTexts = topLevelBlockTexts(after);
  const orderChanged = beforeTexts.some((t, i) => t !== afterTexts[i]);
  if (!orderChanged) {
    return false;
  }
  // Same count and the order changed: a reorder iff the multiset of block texts
  // is unchanged (just shuffled). If the multiset differs, a block's text changed
  // — that's a text edit, keep it debounced.
  const sorted = (xs: string[]) => [...xs].sort();
  const a = sorted(beforeTexts);
  const b = sorted(afterTexts);
  return a.every((t, i) => t === b[i]);
}
